import { useMemo, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, firstErrorMessage } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import { useToast } from "../../ui";
import {
  uniqueIds,
  userMap,
  useUserDirectoryQuery,
} from "../shared/userDirectory";

type Note = components["schemas"]["NoteResponse"];

export function useTranscriptNotes(transcriptId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [noteDraft, setNoteDraft] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteBody, setEditingNoteBody] = useState("");
  const notes = useListQuery("/v1/notes", {
    params: { transcript_id: transcriptId },
    queryKey: queryKeys.notes.list({ transcript_id: transcriptId }),
  });
  const userIds = uniqueIds(
    notes.items.flatMap((note) => [note.author_id, note.edited_by]),
  );
  const users = useUserDirectoryQuery(userIds.join(","), "transcript-notes");
  const noteUsersById = useMemo(
    () => userMap(users.data?.items ?? []),
    [users.data?.items],
  );

  const handlers = (
    successMessage: string,
    errorMessage: string,
    onDone?: () => void,
  ) => ({
    onSuccess: async () => {
      onDone?.();
      toast.success(successMessage);
      await queryClient.invalidateQueries({ queryKey: queryKeys.notes.all });
    },
    onError: (error: Error) => {
      toast.error(errorMessage, {
        description: firstErrorMessage(error) ?? undefined,
      });
    },
  });

  const create = useMutation({
    mutationFn: async (body: string) => {
      const { error } = await apiClient.POST("/v1/notes", {
        body: { body, locator: null, transcript_id: transcriptId },
      });
      if (error) throw new ApiRequestError(error);
    },
    ...handlers("Note added", "Could not add note", () => {
      setNoteDraft("");
    }),
  });
  const save = useMutation({
    mutationFn: async ({ body, note }: { body: string; note: Note }) => {
      const { error } = await apiClient.PATCH("/v1/notes/{id}", {
        body: { body, locator: note.locator },
        params: { path: { id: note.id } },
      });
      if (error) throw new ApiRequestError(error);
    },
    ...handlers("Note updated", "Could not update note", () => {
      setEditingNoteId(null);
      setEditingNoteBody("");
    }),
  });
  const remove = useMutation({
    mutationFn: async (note: Note) => {
      const { error } = await apiClient.DELETE("/v1/notes/{id}", {
        params: { path: { id: note.id } },
      });
      if (error) throw new ApiRequestError(error);
    },
    ...handlers("Note deleted", "Could not delete note"),
  });

  return {
    editingNoteBody,
    editingNoteId,
    isCreatingNote: create.isPending,
    isDeletingNote: remove.isPending,
    isSavingNote: save.isPending,
    mutationError: firstErrorMessage(create.error, save.error, remove.error),
    noteDraft,
    noteUsersById,
    notes,
    onCancelEditNote: () => {
      setEditingNoteId(null);
      setEditingNoteBody("");
    },
    onCreateNote: () => {
      const body = noteDraft.trim();
      if (body) create.mutate(body);
    },
    onDeleteNote: (note: Note) => {
      remove.mutate(note);
    },
    onEditNoteBodyChange: setEditingNoteBody,
    onNoteDraftChange: setNoteDraft,
    onSaveNote: (note: Note) => {
      const body = editingNoteBody.trim();
      if (body) save.mutate({ body, note });
    },
    onStartEditNote: (note: Note) => {
      setEditingNoteId(note.id);
      setEditingNoteBody(note.body);
    },
  };
}
